/**
 * FPR Round Card Component
 *
 * FP Race specific round card with:
 * - Flashpoint options list
 * - Selected flashpoint (when round starts)
 * - Simple player list (no roles)
 */

import { memo, useCallback } from 'react';
import {
  Round,
  Player,
  isFprSettings,
  FprRoundSettings,
  FLASHPOINT_DIFFICULTY_LABELS,
  canAdminDo,
  GameType,
  type GameStatus,
} from '@swtorbit/shared-socket-events';
import { getRoundStatusColor, useRoundTimer } from '@swtorbit/shared-react-hooks';
import { useGameContext } from '../../contexts/GameContext';
import { useFprContext } from '../../contexts/FprContext';
import { RoundCardBase, RoundActionsBar } from '../round';
import { FprPlayerItem } from './FprPlayerItem';
import { useFlashpointCatalogue } from '../../hooks/useFlashpointCatalogue';

/**
 * Type-safe helper to get FPR settings from a round.
 */
function getFprSettings(round: Round): FprRoundSettings | undefined {
  return isFprSettings(round.settings) ? round.settings : undefined;
}

// Admin Timer Display Component for FPR
function FprTimerDisplay({ round }: { round: Round }) {
  const { timerState, displayTime, maxDisplayTime, isUnlimited, isActive, phase } = useRoundTimer(round);

  if (!isActive || !timerState) {
    return null;
  }

  // Countdown phase
  if (phase === 'countdown') {
    return (
      <div className="timer timer--lg timer--boxed timer--soft timer--amber">
        <span className="timer__label">COUNTDOWN</span>
        <span className="timer__value">{displayTime}</span>
      </div>
    );
  }

  // Active race timer
  return (
    <div className="timer timer--lg">
      <span className="timer__label">RACE TIME</span>
      <span className="timer__value">
        {displayTime}
        {!isUnlimited && <span className="timer__max"> / {maxDisplayTime}</span>}
      </span>
    </div>
  );
}

export interface FprRoundCardProps {
  round: Round;
  index: number;
  hasActiveRound: boolean;
  isLoaded: boolean;
  gamePlayers: Player[];
  onStart: (index: number) => void;
  onEnd: (index: number) => void;
  onCancel: (index: number) => void;
  onDisable: (index: number) => void;
  onDelete: (index: number) => void;
  onEdit: (index: number) => void;
  onLoad: (index: number) => void;
  onUnload: (index: number) => void;
}

export const FprRoundCard = memo(function FprRoundCard({
  round,
  index,
  hasActiveRound,
  isLoaded,
  gamePlayers,
  onStart,
  onEnd,
  onCancel,
  onDisable,
  onDelete,
  onEdit,
  onLoad,
  onUnload,
}: FprRoundCardProps) {
  const { selectedGame } = useGameContext();
  const { failedScoringRounds, retryFprScoring, forceCompleteFprRound } = useFprContext();
  const { byId: flashpointsById } = useFlashpointCatalogue();

  const scoringFailed = failedScoringRounds.has(index);

  const handleRetryScoring = useCallback(() => {
    if (!selectedGame) return;
    retryFprScoring(
      { roomCode: selectedGame.roomCode, roundIndex: index },
      (resp) => {
        if (!resp.success) {
          window.alert(`Retry scoring failed: ${resp.error ?? 'unknown error'}`);
        }
      }
    );
  }, [selectedGame, retryFprScoring, index]);

  const handleForceComplete = useCallback(() => {
    if (!selectedGame) return;
    if (!window.confirm('Force-complete this round WITHOUT scoring? Round will contribute 0 points.')) return;
    forceCompleteFprRound(
      { roomCode: selectedGame.roomCode, roundIndex: index },
      (resp) => {
        if (!resp.success) {
          window.alert(`Force-complete failed: ${resp.error ?? 'unknown error'}`);
        }
      }
    );
  }, [selectedGame, forceCompleteFprRound, index]);

  const settings = getFprSettings(round);

  if (!settings) {
    return <div className="card card--sm card--bg-midnight card--border card--border-navy">Invalid FPR round settings</div>;
  }

  // Ensure flashpointOptions is always an array (defensive check)
  const flashpointOptions = settings.flashpointOptions ?? [];

  const isRaceActive = round.status === 'countdown' || round.status === 'racing';
  const canEnd = isRaceActive;

  // Derive game-lifecycle-aware button visibility
  const gameStatus = selectedGame?.status as GameStatus | undefined;
  const canLoad = gameStatus !== undefined
    && canAdminDo(GameType.FP_RACE, gameStatus, 'canLoadRound')
    && round.status === 'setup'
    && !isLoaded;
  const canUnloadOrStart = gameStatus !== undefined
    && canAdminDo(GameType.FP_RACE, gameStatus, 'canLoadRound')
    && round.status === 'setup'
    && isLoaded;
  const canDelete = round.status === 'setup' && !isLoaded;
  const canEdit = round.status === 'setup' && !isLoaded;
  const canDisable = round.status === 'completed';

  return (
    <RoundCardBase
      index={index}
      status={round.status}
      disabled={round.disabled}
      borderColor={getRoundStatusColor(round.status)}
      timer={isRaceActive && <FprTimerDisplay round={round} />}
      actions={
        <RoundActionsBar
          leftActions={
            <>
              {canDelete && (
                <button onClick={() => onDelete(index)} className="btn btn--red">
                  🗑️ Delete
                </button>
              )}
              {canEdit && (
                <button onClick={() => onEdit(index)} className="btn btn--aqua">
                  ✏️ Edit
                </button>
              )}
              {canDisable && (
                <button onClick={() => onDisable(index)} className={`btn ${round.disabled ? 'btn--green' : 'btn--amber'}`}>
                  {round.disabled ? '✅ Enable' : '🚫 Disable'}
                </button>
              )}
            </>
          }
          rightActions={
            <>
              {canLoad && (
                <button onClick={() => onLoad(index)} className="btn btn--aqua">📥 Load</button>
              )}
              {canUnloadOrStart && (
                <button onClick={() => onUnload(index)} className="btn btn--outline btn--slate">📤 Unload</button>
              )}
              {canUnloadOrStart && !hasActiveRound && (
                <button onClick={() => onStart(index)} className="btn btn--green">🏁 Start Round</button>
              )}
              {canUnloadOrStart && hasActiveRound && (
                <button disabled className="btn btn--ghost" title="Another round is active">🏁 Start Round (Blocked)</button>
              )}
              {canEnd && (
                <>
                  <button onClick={() => onEnd(index)} className="btn">🏆 End Round</button>
                  <button onClick={() => onCancel(index)} className="btn btn--red">❌ Cancel</button>
                </>
              )}
            </>
          }
        />
      }
      footer={scoringFailed && (
        <div className="bg-space bg-o-50 border-md border-red rounded-sm p-xs mt-xs d-flex flex-column gap-xs">
          <p className="text-red fw-bold m-0">
            ⚠️ Scoring failed after 3 retries — round is not yet complete.
          </p>
          <div className="d-flex flex-wrap gap-xs">
            <button onClick={handleRetryScoring} className="btn">
              🔁 Retry Scoring
            </button>
            <button onClick={handleForceComplete} className="btn btn--red">
              ⏭️ Force Complete (no scoring)
            </button>
          </div>
        </div>
      )}
    >
      {/* Flashpoint Options / Selection Display */}
      {settings.selectedFlashpoint ? (
        // Round has started - show selected flashpoint
        <>
          <p className="fw-bold">🎯 Selected Flashpoint:</p>
          <div className="bg-space bg-o-50 border-md border-blue-light rounded-sm p-xs d-flex align-items-center gap-xs">
            <span className="fw-bold fs-lg">
              {settings.selectedFlashpointDef?.name ??
                flashpointsById.get(settings.selectedFlashpoint.flashpointId)?.name ??
                settings.selectedFlashpoint.flashpointId}
            </span>
            <span className="fs-sm o-70">
              ({FLASHPOINT_DIFFICULTY_LABELS[settings.selectedFlashpoint.difficulty]})
            </span>
          </div>
          {flashpointOptions.length > 1 && (
            <p className="fs-sm o-70">
              Randomly selected from {flashpointOptions.length} options
            </p>
          )}
        </>
      ) : (
        // Round not started - show options
        <>
          <p className="fw-bold">
            Flashpoint Options ({flashpointOptions.length})
            {flashpointOptions.length > 1 && (
              <span className="fs-sm o-70 fw-normal"> — one will be randomly selected</span>
            )}
          </p>
          <ul className="d-flex flex-column gap-2xs p-0 m-0" style={{ listStyle: 'none' }}>
            {flashpointOptions.map((option) => {
              const fp = flashpointsById.get(option.flashpointId);
              return (
                <li
                  key={`${option.flashpointId}-${option.difficulty}`}
                  className="bg-space bg-o-50 border-md border-blue-light rounded-sm p-xs d-flex justify-content-between align-items-center"
                >
                  <span>{fp?.name ?? option.flashpointId}</span>
                  <span className="fs-sm o-70">
                    {FLASHPOINT_DIFFICULTY_LABELS[option.difficulty]}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      <p className="fw-bold mt-xs">Racers ({round.players.length}):</p>
      <div className="d-flex flex-column gap-2xs">
        {round.players.map(player => (
          <FprPlayerItem
            key={player.playerId}
            player={player}
            gamePlayers={gamePlayers}
          />
        ))}
      </div>
    </RoundCardBase>
  );
});
